import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Trophy, User as UserIcon, Award, TrendingUp } from 'lucide-react';
import { useAuthStore } from '@fog-chess/core';
import { getLeaderboard, getMyRank } from '@fog-chess/core';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

/**
 * LeaderboardScreen - TanStack Query driven (server state boundary respected).
 *
 * - Fetches Top 100 via getLeaderboard({ limit: 100 })
 * - Fetches current user's rank via getMyRank()
 * - Highlights the authenticated user's row
 * - Clean shadcn/ui table with stage badges
 */

interface LeaderboardEntry {
  rank: number;
  user_id: string;
  username: string;
  score: number;
  stage: number;
}

function useLeaderboard() {
  return useQuery({
    queryKey: ['leaderboard', 100],
    queryFn: () => getLeaderboard({ limit: 100 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

function useMyRank() {
  return useQuery({
    queryKey: ['my-rank'],
    queryFn: getMyRank,
    staleTime: 30_000,
    refetchInterval: 60_000,
    retry: 1,
  });
}

function StageBadge({ stage }: { stage: number }) {
  const colors = [
    'bg-slate-500/15 text-slate-600 dark:text-slate-400',
    'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
    'bg-sky-500/15 text-sky-700 dark:text-sky-400',
    'bg-amber-500/15 text-amber-700 dark:text-amber-400',
    'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  ];
  const labels = ['Stage 1', 'Stage 2', 'Stage 3', 'Stage 4', 'Stage 5'];
  const idx = Math.max(0, Math.min(stage - 1, 4));
  return (
    <Badge variant="outline" className={cn('gap-1', colors[idx])}>
      <Award className="h-3 w-3" />
      {labels[idx]}
    </Badge>
  );
}

function LeaderboardRow({
  entry,
  isCurrentUser,
}: {
  entry: LeaderboardEntry;
  isCurrentUser: boolean;
}) {
  const rankIcon = entry.rank === 1 ? (
    <Trophy className="h-4 w-4 text-amber-500" />
  ) : entry.rank === 2 ? (
    <Trophy className="h-4 w-4 text-slate-400" />
  ) : entry.rank === 3 ? (
    <Trophy className="h-4 w-4 text-amber-700" />
  ) : null;

  return (
    <TableRow
      className={cn(
        'transition-colors',
        isCurrentUser && 'bg-primary/5',
        entry.rank <= 3 && 'font-medium',
      )}
    >
      <TableCell className="font-mono text-lg">
        {rankIcon ? (
          <span className="flex items-center gap-1">{rankIcon}</span>
        ) : (
          <span>#{entry.rank}</span>
        )}
      </TableCell>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {isCurrentUser && <UserIcon className="h-4 w-4 text-primary" />}
          <span>{entry.username}</span>
          {isCurrentUser && (
            <Badge variant="secondary" className="ml-2 text-xs">
              You
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell className="text-right font-mono tabular-nums text-lg">
        <span className={cn('font-semibold', entry.score >= 0 && 'text-emerald-600 dark:text-emerald-400')}>
          {entry.score > 0 ? '+' : ''}{entry.score}
        </span>
      </TableCell>
      <TableCell className="text-right">
        <StageBadge stage={entry.stage} />
      </TableCell>
    </TableRow>
  );
}

function LeaderboardTable({
  entries,
  currentUserId,
}: {
  entries: LeaderboardEntry[];
  currentUserId: string | undefined;
}) {
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <Trophy className="h-12 w-12 mb-3 opacity-30" />
        <p>No leaderboard data available</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="w-16">Rank</TableHead>
            <TableHead>Player</TableHead>
            <TableHead className="text-right w-32">Score</TableHead>
            <TableHead className="text-right w-36">Stage</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {entries.map((entry) => (
            <LeaderboardRow
              key={entry.user_id}
              entry={entry}
              isCurrentUser={entry.user_id === currentUserId}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function LeaderboardSkeleton() {
  return (
    <div className="rounded-lg border overflow-hidden">
      <Table>
        <TableHeader className="bg-muted/50">
          <TableRow>
            <TableHead className="w-16"><Skeleton className="h-4 w-8" /></TableHead>
            <TableHead><Skeleton className="h-4 w-32" /></TableHead>
            <TableHead className="text-right w-32"><Skeleton className="h-4 w-20" /></TableHead>
            <TableHead className="text-right w-36"><Skeleton className="h-4 w-24" /></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell><Skeleton className="h-4 w-8" /></TableCell>
              <TableCell><Skeleton className="h-4 w-32" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-20" /></TableCell>
              <TableCell className="text-right"><Skeleton className="h-4 w-24" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export function LeaderboardScreen() {
  const user = useAuthStore((s) => s.user);
  const { data: leaderboardData, isLoading: loadingLeaderboard } = useLeaderboard();
  const { data: myRankData, isLoading: loadingMyRank } = useMyRank();

  const entries = leaderboardData?.entries ?? [];
  const myRank = myRankData?.rank; // used in badge display

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Leaderboard</h1>
            <p className="text-muted-foreground">
              Top 100 players by FTPES score
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="gap-1">
              <TrendingUp className="h-3 w-3" />
              <span>Live ranking</span>
            </Badge>
            {myRank && (
              <Badge variant="default" className="gap-1">
                <Trophy className="h-3 w-3" />
                Your rank: <strong>#{myRank}</strong>
              </Badge>
            )}
          </div>
        </div>
      </motion.div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Top 100</CardTitle>
        </CardHeader>
        <CardContent>
          {(loadingLeaderboard || loadingMyRank) ? (
            <LeaderboardSkeleton />
          ) : (
            <LeaderboardTable
              entries={entries}
              currentUserId={user?.uuid}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}